import type { CustomSeriesRenderItemAPI } from 'echarts';
import { graphic } from 'echarts';

import type {
  CompressorForecastState,
  CompressorScheduleForecast,
  Transition,
} from '@repo/sdk/models/components';

import {
  BAR_BORDER_RADIUS,
  CHART_GREEN_RGB,
  RAMP_BORDER_COLOR,
  RAMP_BORDER_WIDTH,
  RAMP_GRADIENT_MIN_OPACITY,
  STAGE_COLORS,
  STAGE_LABELS,
  STAGE_VALUES,
  TRIM_STRIPE_COLOR,
  TRIM_STRIPE_SPACING,
  TRIM_STRIPE_WIDTH,
} from './compressor-schedule-chart.constants';

export interface ScheduleBarSegment {
  startTime: number;
  endTime: number;
  rowIndex: number;
  value: number;
  transition: Transition;
  color: string;
  displayValue: string;
}

/**
 * Transform the BFF CompressorScheduleForecast response into an array of
 * ScheduleBarSegment objects suitable for the ECharts custom series.
 *
 * `compressorOrder` is the authoritative row ordering from the sequencer table.
 * Each segment's `rowIndex` maps to a position in that array, ensuring the
 * chart rows align with the table rows regardless of the BFF response order.
 *
 * For each compressor, consecutive steps define time-ranged bars. The BFF
 * always appends a synthetic final step at the `until` boundary, so every
 * real step has a successor. Segments with OFF or UNAVAILABLE are excluded.
 */
export function transformForecastToSegments(
  forecast: CompressorScheduleForecast,
  compressorOrder: string[]
): ScheduleBarSegment[] {
  const { compressors, steps } = forecast;
  if (steps.length < 2) return [];

  const orderIndex = new Map(compressorOrder.map((name, idx) => [name, idx]));

  const segments: ScheduleBarSegment[] = [];

  for (let compIdx = 0; compIdx < compressors.length; compIdx++) {
    const rowIndex = orderIndex.get(compressors[compIdx]);
    if (rowIndex === undefined) continue;

    let prev: ScheduleBarSegment | null = null;

    for (let stepIdx = 0; stepIdx < steps.length - 1; stepIdx++) {
      const currentStep = steps[stepIdx];
      const nextStep = steps[stepIdx + 1];
      const state: CompressorForecastState | undefined = currentStep.states[compIdx];
      if (!state) continue;

      const { value, transition } = state;
      if (value === STAGE_VALUES.OFF || value === STAGE_VALUES.UNAVAILABLE) {
        prev = null;
        continue;
      }

      const color = STAGE_COLORS[value];
      if (!color) {
        prev = null;
        continue;
      }

      if (prev && prev.value === value && prev.transition === transition) {
        prev.endTime = nextStep.timestamp.getTime();
        continue;
      }

      const segment: ScheduleBarSegment = {
        startTime: currentStep.timestamp.getTime(),
        endTime: nextStep.timestamp.getTime(),
        rowIndex,
        value,
        transition,
        color,
        displayValue: STAGE_LABELS[value] ?? 'Unknown',
      };
      segments.push(segment);
      prev = segment;
    }
  }

  return segments;
}

/** Indices into the ECharts data tuple. */
const DATA = {
  START_TIME: 0,
  END_TIME: 1,
  ROW_INDEX: 2,
  COLOR: 3,
  TRANSITION: 4,
  DISPLAY_VALUE: 5,
  VALUE: 6,
} as const;

/**
 * Convert a ScheduleBarSegment to the ECharts data tuple consumed by
 * the renderItem function.
 */
export function segmentToTuple(
  s: ScheduleBarSegment
): [number, number, number, string, string, string, number] {
  return [s.startTime, s.endTime, s.rowIndex, s.color, s.transition, s.displayValue, s.value];
}

/**
 * Resolve the fill for a bar segment.
 * Steady segments get a solid color; ramping segments get a horizontal
 * gradient that fades in the direction of the transition.
 */
function resolveFill(color: string, transition: string) {
  if (transition === 'ramping_up') {
    return new graphic.LinearGradient(0, 0, 1, 0, [
      { offset: 0, color: `rgba(${CHART_GREEN_RGB}, ${RAMP_GRADIENT_MIN_OPACITY})` },
      { offset: 1, color: `rgba(${CHART_GREEN_RGB}, 1)` },
    ]);
  }
  if (transition === 'ramping_down') {
    return new graphic.LinearGradient(0, 0, 1, 0, [
      { offset: 0, color: `rgba(${CHART_GREEN_RGB}, 1)` },
      { offset: 1, color: `rgba(${CHART_GREEN_RGB}, ${RAMP_GRADIENT_MIN_OPACITY})` },
    ]);
  }
  return color;
}

/**
 * Generate diagonal dark stripes for Trim segments, matching the
 * sequencer table's `trimBackground` pattern (135deg / `/` direction).
 */
function generateTrimStripes(x: number, y: number, width: number, height: number) {
  const lines: object[] = [];
  const span = width + height;
  const overscan = TRIM_STRIPE_WIDTH;
  const lineStyle = { stroke: TRIM_STRIPE_COLOR, lineWidth: TRIM_STRIPE_WIDTH };

  // Draw past the bar bounds, then rely on clipPath to trim. This avoids
  // anti-aliasing gaps where stripe ends meet the top/bottom bar edges.
  for (let offset = -height - overscan; offset < span + overscan; offset += TRIM_STRIPE_SPACING) {
    lines.push({
      type: 'line' as const,
      shape: {
        x1: x + offset + overscan,
        y1: y - overscan,
        x2: x + offset - height - overscan,
        y2: y + height + overscan,
      },
      style: lineStyle,
      silent: true,
    });
  }

  return lines;
}

/**
 * ECharts custom series renderItem function for compressor schedule bars.
 *
 * Visual encoding:
 *  - Base (steady)  → solid green
 *  - Trim (steady)  → green with dark diagonal stripes
 *  - Ramping up     → horizontal gradient (transparent → green)
 *  - Ramping down   → horizontal gradient (green → transparent)
 *  - Trim + ramping → gradient + stripes
 */
export function createScheduleRenderItem() {
  return (_params: unknown, api: CustomSeriesRenderItemAPI) => {
    const startTime = api.value(DATA.START_TIME) as number;
    const endTime = api.value(DATA.END_TIME) as number;
    const rowIndex = api.value(DATA.ROW_INDEX) as number;
    const color = api.value(DATA.COLOR) as string;
    const transition = api.value(DATA.TRANSITION) as string;
    const value = api.value(DATA.VALUE) as number;

    const [startX, yPixel] = api.coord([startTime, rowIndex]);
    const [endX] = api.coord([endTime, rowIndex]);

    const [, y0] = api.coord([0, 0]);
    const [, y1] = api.coord([0, 1]);
    const rowHeight = Math.abs(y1 - y0) || 24;
    const barHeight = Math.min(24, rowHeight * 0.7);
    const width = Math.max(endX - startX, 1);

    const x = startX;
    const y = yPixel - barHeight / 2;

    const fill = resolveFill(color, transition);

    const baseRect = {
      type: 'rect' as const,
      shape: { x, y, width, height: barHeight, r: BAR_BORDER_RADIUS },
      style: {
        fill,
        stroke: RAMP_BORDER_COLOR,
        lineWidth: RAMP_BORDER_WIDTH,
      },
      silent: true,
    };

    const isTrim = value === STAGE_VALUES.TRIM;
    if (!isTrim) {
      return baseRect;
    }

    const trimStripes = generateTrimStripes(x, y, width, barHeight);

    return {
      type: 'group' as const,
      children: [baseRect, ...trimStripes],
      clipPath: {
        type: 'rect' as const,
        shape: { x, y, width, height: barHeight },
      },
    };
  };
}
